import React, { createContext } from "react";
import { SidebarProps } from "react-sidebar";
import IErrorDTO from "../models/DTO/IErrorDTO";
import NotificationLevelEnum from "@models/NotificationLevelEnum";

/**
 * Note: Anytime you have a specific value such as isMenuOpen or isEcommView that ties directly to a useState variable
 * You will need to add that variable to the dependency array in the useEffect in App.tsx to have it refresh the
 * Context Model values
 */
export interface IUiContextModel {
    forceOnline: boolean;
    isOnline: boolean;
    /**
     * Function to set the sidebar component in the sidebar that overlays all sections on the right hand side
     * @param component
     */
    setSidebarComponent: (component: React.Component | React.JSX.Element | null) => void;
    /**
     * Function toggle the sidebar that overlays all sections on the right hand side
     * @param shouldBeVisible
     * @param sidebarProps
     */
    toggleSidebar: (shouldBeVisible?: boolean, sidebarProps?: SidebarProps, disableScroll?: boolean) => void;
    getSidebarState: () => boolean;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    showNotification: (options: { message: string; level: NotificationLevelEnum }) => void;
    toggleRightSide: (shouldBeVisible?: boolean) => void;
    setRightSideComponent: (component: React.Component | React.JSX.Element | null) => void;
    setSubHeaderComponent: (component: React.Component | React.JSX.Element | null) => void;
    subHeaderComponent: React.Component | React.JSX.Element | null;
    setRightSideHeaderComponent: (component: React.Component | React.JSX.Element | null) => void;
    setActivityIndicator: (activityOn: boolean) => void;
    toggleMenu: () => void;
    menuOpen: boolean;
    isMenuOpen: () => boolean;
    toggleMenuIconMode: () => void;
    menuIconMode: boolean;
    setMenuIconMode: (menuIconMode: boolean) => void;
    isMenuIconMode: () => boolean;
    showHiddenFeatures: boolean;
    setShowHiddenFeatures: (showHiddenFeatures: boolean) => void;
    disableIdleTimeout: boolean;
    setDisableIdleTimeout: (disable: boolean) => void;
    getIdleTimeRemaining: () => number;
    setIdleTimeRemaining: (seconds: number) => void;
    setModalComponent: (component: React.Component | React.JSX.Element | null) => void;
    setModalSetup: (options: { modalContentClass?: string; modalOverlayClass?: string; modalStyle?: any }) => void;
    resetModalClassesAndStyles: () => void;
    showModal: (shouldBeVisible?: boolean) => void;
    modalIsOpen: boolean;
    isEcommView: boolean;
    setIsEcommView: (isEcommView: boolean) => void;
    cartItemCount: number;
    setCartItemCount: (count: number) => void;
    isCheckoutView: boolean;
    setIsCheckoutView: (isCheckoutView: boolean) => void;
    modalCloseOnEsc: boolean;
    setModalCloseOnEsc: (modalCloseOnEsc: boolean) => void;
    setModalCloseOnOverlayClick: (modalCloseOnOverlayClick: boolean) => void;
    /** Ecommerce gate: the signed-in user has acknowledged the supplier's current required login message. */
    loginMessageAcknowledged: boolean;
}

const UiContext = createContext<IUiContextModel>({ forceOnline: false, menuOpen: false, isOnline: true } as IUiContextModel);

export default UiContext;
